import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import { Typography, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { GatsbyImage } from 'gatsby-plugin-image';
import Markdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import { ThemeProvider } from '@material-ui/core';
import ArrowBack from '@material-ui/icons/ArrowBack';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/lab/Alert';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import { Helmet } from 'react-helmet';
import theme from '../../theme';
import Divider from '../../components/Divider';
import Button from '../../components/Button';
import PaypalButton from '../../components/PaypalButton';
import ShippingInformation from '../../components/ShippingInformation';
import { getUrlParams, Statuses } from '../../utils/urlUtils';
import { post } from '../../utils/apiUtils';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    justifyContent: 'center',
  },
  gridContainer: {
    display: 'flex',
    alignContent: 'center',
    padding: '1rem 5rem 5rem 5rem',
  },
  product: {
    display: 'flex',
  },
  productTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },

  productTitle: {
    fontSize: '2.5rem',
    fontWeight: 600,
  },
  productDescription: {
    fontSize: '1.2rem',
    paddingTop: '2rem',
  },
  price: {
    fontSize: '1.5rem',
    padding: '1rem 0 1rem 0',
  },
  image: {
    maxWidth: '20rem',
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '32rem',
    maxHeight: '32rem',
    border: '0.1rem solid grey',
    borderRadius: '0.5rem',
  },
  details: {
    textAlign: 'left',
    padding: '5rem 5rem',
  },
  backWrapper: {
    display: 'flex',
    justifyContent: 'fex-start',
    margin: '2rem 0 2rem 0',
  },
  imageContainerGridItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export const query = graphql`
  query ProductQuery($slug: String!) {
    strapiProduct(slug: { eq: $slug }) {
      strapiId
      slug
      title
      description
      details
      price
      image {
        localFile {
          childImageSharp {
            gatsbyImageData(width: 250)
          }
        }
      }
    }
  }
`;

export interface ProductPageProps {
  title: string;
  description: string;
  image: string;
  slug: string;
}

export interface ShippingInformationType {
  name: string;
  email: string;
}

export interface ShippingInformationErrorsType {
  name: string;
  email: string;
}

const Product = ({ data }) => {
  const {
    status: initialStatus,
    name: initialName = '',
    email: initialEmail = '',
  } = getUrlParams();
  const [status, setStatus] = useState<Statuses>(initialStatus || 'VIEW');
  let _referenceId = '';
  const [shippingInformation, setShippingInformation] =
    useState<ShippingInformationType>({
      name: initialName,
      email: initialEmail,
    });
  const [shippingInformationError, setShippingInformationError] =
    useState<ShippingInformationErrorsType>({
      name: '',
      email: '',
    });
  // PayPal checkout button cannot access the lates state unless we rerender. We use this state to do that
  const [validInputs, setValidInputs] = useState(false);

  const { strapiId, title, description, price, image, details } =
    data.strapiProduct;
  //console.log(data.strapiProduct);
  const classes = useStyles();
  const options = [{ name: 'Single Shard' }];

  const handleShippingInformationChange = (_name, _value) => {
    if (!_name) return;
    const nameRegex = /^[a-zA-Z0-9_.-]+$/;
    const emailRegex = /^\S+@\S+\.\S+$/;
    const regex = _name === 'email' ? emailRegex : nameRegex;
    let error = '';
    if (!regex.test(_value)) {
      console.log('set valid', false);
      setValidInputs(false);
      if (_name === 'name') {
        error = 'Invalid name';
      } else if (_name === 'email') {
        error = 'Invalid email';
      } else {
        error = 'Required';
      }
    }

    setShippingInformation({
      ...shippingInformation,
      [_name]: _value,
    });
    setShippingInformationError({
      ...shippingInformationError,
      [_name]: error,
    });

    if (
      nameRegex.test(shippingInformation.name) &&
      emailRegex.test(shippingInformation.email)
    ) {
      setValidInputs(true);
    }
  };

  const createOrder = async (data, actions) => {
    try {
      const { data: { referenceId } } = await post('/order/create', {
        ...shippingInformation,
        productId: strapiId
      });
      _referenceId = referenceId;
      if (!referenceId) throw new Error('Reference Id cannot be empty');
      return actions.order.create({
        purchase_units: [
          {
            amount: {
              value: price,
            },
            reference_id: referenceId
            // custom_id
            // invoice_id: '',
          },
        ],
      });
    } catch (error) {
      console.log(error);
      setStatus('FAILED');
    }
  };

  const getOrder = async (data) => {
    try {
      const { success } = await post('/order/getorder', {
        orderId: data.orderID,
        referenceId: _referenceId
      });
      if (!success) {
        throw new Error('paypal transaction failed')
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const onApprove = async (data, actions) => {
    try {
      await getOrder(data); // TODO : check if the payment has been completed and email the download link
      setStatus('SHIPPED');
    } catch (error) {
      setStatus('FAILED');
    }
  };

  const onInit = (data, actions) => {
    validInputs ? actions.enable() : actions.disable();
  };

  /**
   * create the order and send the email - for free products
   * @param data
   * @param actions
   */
  const sendScriptToEmail = async (data, actions) => {
    if (validInputs) {
      try {
        await createOrder(data, actions);

        // check whether order created exists and email the download link
        const { success } = await post('/order/getorder', {
          orderId: data.orderID,
          referenceId: _referenceId
        });
        success ? setStatus('SHIPPED') : setStatus('FAILED');
      } catch (error) {
        setStatus('FAILED');
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Layout pageName={title}>
        <SEO title={title} description={description} />
        <Helmet>
      {/* Global site tag (gtag.js) - Google Analytics */}
      <script async src="https://www.googletagmanager.com/gtag/js?id=UA-118777783-1"></script>
      <script type="text/javascript">
        {`window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());

        gtag('config', 'UA-118777783-1');`}
      </script>
      </Helmet>
        <div className={classes.root}>
          <Grid container className={classes.gridContainer}>
            <Grid item md={12}>
              <Link href="/products" className={classes.backWrapper}>
                <ArrowBack /> Back
              </Link>
            </Grid>
            <Grid item md={6} className={classes.imageContainerGridItem}>
              <div className={classes.imageContainer}>
                <GatsbyImage
                  style={{
                    gridArea: '1/1',
                  }}
                  alt={`Picture for ${title} article`}
                  image={image.localFile.childImageSharp.gatsbyImageData}
                />
                <img
                  src={image.localFile.publicURL}
                  className={classes.image}
                />
              </div>
            </Grid>
            <Grid item md={6} className={classes.productTextContainer}>
              <Typography variant="h1" className={classes.productTitle}>
                {title}
              </Typography>
              <Typography variant="h2" className={classes.productDescription}>
                {description}
              </Typography>
              {price != 0 && (
                <Typography className={classes.price}>${price}</Typography>
              )}
              {price == 0 && (
                <Typography className={classes.price}>Free</Typography>
              )}
              {/* <Divider light size='small'/> */}
              {/* <Typography>Product Type</Typography> */}
            
              <Divider visibility={false} size="large" />
              {status === 'VIEW' && (
                <Button onClick={() => setStatus('BUY_NOW')}>Buy Now</Button>
              )}
              {(status === 'BUY_NOW' || status === 'FAILED') && (
                <>
                  <ShippingInformation
                    onChange={handleShippingInformationChange}
                    name={shippingInformation.name}
                    email={shippingInformation.email}
                    nameError={shippingInformationError.name}
                    emailError={shippingInformationError.email}
                  />
                  <Divider visibility={false} size="small" />
                  {/* The following code looks stupid :D.  But PayPal `Smart` checkout button cannot react the react state properly. So we have to rerender it when the inputs are valid*/}
                  {validInputs && price != 0 && (
                    <PaypalButton
                      amount={price}
                      createOrder={createOrder}
                      onApprove={onApprove}
                      onInit={onInit}
                    />
                  )}
                  {!validInputs && price != 0 && (
                    <PaypalButton
                      amount={price}
                      createOrder={createOrder}
                      onApprove={onApprove}
                      onInit={onInit}
                    />
                  )}
                  {/*For Free Products-Just click to download the script*/}
                  {price == 0 && (
                    <Button onClick={sendScriptToEmail}>Get Script</Button>
                  )}
                </>
              )}
              {status === 'SHIPPED' && (
                <Alert severity="success">
                  {`Product will be shipped to ${shippingInformation.email || 'your email'
                    }`}
                </Alert>
              )}
              {status === 'FAILED' && (
                <Alert severity="error">
                  Something went wrong! Please try again.
                </Alert>
              )}
            </Grid>
            <Grid item>
              <Markdown
                children={details}
                className={classes.details}
                rehypePlugins={[rehypeHighlight]}
              />
            </Grid>
          </Grid>
        </div>
      </Layout>
    </ThemeProvider>
  );
};

export default Product;
